import { Injectable } from '@angular/core';
import { ListHelperService, ProfileService, StoreService, ConfigurationsService, LocalesService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { KeplerPlayerService } from 'app/kepler/services/kepler-player.service';
import { DEFAULT_RESPONSE_TABLE } from 'app/kepler/assets/constants';
import { tap } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class KeplerPlayerProgressListService {
  userRole: any;
  constructor(
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public keplerPlayerListService: KeplerPlayerService,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService,
    public router: Router
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}, player) {
    const dataSource = new TableDatasource({
      sort: 'index',
      columns: await this.getColumns(),
      load: (props) => this.fetch(props, dataSource, player).subscribe(() => dataSource.refresh()),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource, player: any) {
    const { ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource);

    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      $filter
    };
    return this.keplerPlayerListService.getPlayerProgress(player, paginationOptions).pipe(
      tap((response) => {
        dataSource.docs = response?.docs || DEFAULT_RESPONSE_TABLE.docs;
        dataSource.total = response?.total || DEFAULT_RESPONSE_TABLE.total;
        dataSource.limit = response?.limit || DEFAULT_RESPONSE_TABLE.limit;
        dataSource.page = response?.page || DEFAULT_RESPONSE_TABLE.page;
        dataSource.pages = response?.pages || DEFAULT_RESPONSE_TABLE.pages;
      })
    );
  }

  async getColumns() {
    const columns = [
      {
        source: 'email',
        label: this.translate.instant('nouns.email')
      },
      {
        source: 'index',
        style: { width: '150px' },
        label: this.translate.instant('kepler.kpiNum'),
        sortable: true
      },
      {
        source: 'firstConnection',
        label: this.translate.instant('reports.firstConnection'),
        sortable: true,
        dataType: DataTypes.datetime,
        valueParser: (value, player) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, player.timezone, format);
        }
      },
      {
        source: 'lastConnection',
        label: this.translate.instant('reports.lastConnection'),
        valueParser: (value, player) => {
          const format = DateTime.DATETIME_MED_WITH_SECONDS;
          return this.listHelper.parseDate(value, player.timezone, format);
        }
      },
      {
        source: 'totalKPIPoints',
        label: this.translate.instant('kepler.player.header.kpis'),
        sortable: true,
        valueParser: (value) => {
          return value == null ? '' : '' + value;
        }
      },
      {
        source: 'totalPoints',
        label: this.translate.instant('kepler.player.header.totalPoints'),
        tooltip: await this.getInfoText('kepler.tableColumns.totalPoints'),
        sortable: true,
        valueParser: (value) => {
          return value == null ? '' : '' + value;
        }
      }
    ];
    return columns.filter((col) => col !== null);
  }

  /**
   * Get the text from contentful/configurations
   * @param key
   * @private
   */
  private async getInfoText(key: string): Promise<any> {
    return lastValueFrom(
      this.configurationsService.getInfoMessage$(
        'toolTip',
        'fields.keys',
        key,
        'textoDeAyuda',
        this.localesService.getBrowserLocale(),
        true
      )
    );
  }
}
